import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Delete, Edit } from '@mui/icons-material';
import Title from '../../components/Title';
import AdminsDialog from './AdminsDialog';
import { getRoleIcon } from '../AdminRolesPage/roleIcons';
import {
  deleteAdminAsync,
  getAdminsAsync,
  selectAdmins
} from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { getTabsAsync } from '../../redux/slices/tabsSlice';

const PREFIX = 'AdminsPage';

const classes = {
  header: `${PREFIX}-header`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    marginBottom: 20
  }
});

const AdminsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const citiesIds = useMemo(() => {
    if (isSuperAdmin) {
      return '';
    }

    const cities = (admins.info?.cities ?? []).map((item) => item?._id);
    const citiesIds = cities.join(',').toString()

    return citiesIds;
  }, [isSuperAdmin, admins.info]);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getAdminsAsync());
    }

    if (!isSuperAdmin && citiesIds) {
      dispatch(getAdminsAsync(citiesIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesIds]);

  useEffect(() => {
    if (!isSuperAdmin) {
      dispatch(getTabsAsync());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    adminId?: string;
  }>({ open: false });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<{ id: string; login: string } | null>(null);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (deleteConfirmOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [deleteConfirmOpen, countdown]);

  const openDialog = useCallback(
    (adminId?: string) => () => setDialogState({ open: true, adminId }),
    []
  );

  const closeDialog = useCallback(() => setDialogState({ open: false }), []);

  const openDeleteConfirm = useCallback(
    (adminId: string, adminLogin: string) => () => {
      setAdminToDelete({ id: adminId, login: adminLogin });
      setDeleteConfirmOpen(true);
      setCountdown(3);
    },
    []
  );

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setAdminToDelete(null);
    setCountdown(3);
  }, []);

  const deleteAdmin = useCallback(async () => {
    if (!adminToDelete) return;
    
    await dispatch(deleteAdminAsync(adminToDelete.id));
    dispatch(getAdminsAsync());
    closeDeleteConfirm();
  }, [adminToDelete, dispatch, closeDeleteConfirm]);

  return (
    <Root>
      <Grid
        container
        alignItems='center'
        className={classes.header}
        spacing={4}
      >
        <Grid item>
          <Title>Адміністратори</Title>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            startIcon={<Add />}
            variant='contained'
            onClick={openDialog()}
          >
            Додати
          </Button>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Логін</TableCell>
              <TableCell align='right'>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.items.map(admin => {
              const AdminIcon = getRoleIcon(admin.role?.icon);
              return (
              <TableRow key={admin._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={admin.role?.name || "Адміністратор"} arrow>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 213, 79, 0.12)' 
                            : 'rgba(66, 66, 66, 0.12)',
                          borderRadius: '8px'
                        }}
                      >
                        <AdminIcon sx={{ 
                          fontSize: 24, 
                          color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' 
                        }} />
                      </Avatar>
                    </Tooltip>
                    <span>{admin.login}</span>
                  </Box>
                </TableCell>
                <TableCell align='right'>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title='Редагувати'>
                      <IconButton
                        size='medium'
                        onClick={openDialog(admin._id)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Видалити'>
                      <IconButton
                        size='medium'
                        onClick={openDeleteConfirm(admin._id, admin.login)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AdminsDialog {...dialogState} onClose={closeDialog} />
      
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити адміністратора "{adminToDelete?.login}"?
            {countdown > 0 && (
              <Box sx={{ mt: 2, color: 'warning.main', fontWeight: 500 }}>
                Кнопка стане активною через {countdown} сек...
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>
            Скасувати
          </Button>
          <Button 
            onClick={deleteAdmin} 
            color="error" 
            variant="contained"
            disabled={countdown > 0}
          >
            Видалити
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default AdminsPage;
